import React, { useState } from 'react';
import {
    Box, TextField, Select, MenuItem, FormControl, InputLabel,
    Typography, Collapse, Button,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from '@mui/icons-material';

const REFERENCE_CONFIDENCE_OPTIONS = [
    { value: 'certified_traceable', label: 'Certified calibration standard (NIST-traceable)' },
    { value: 'metrology_lab',       label: 'Certified by accredited metrology lab' },
    { value: 'prior_average',       label: 'Average of prior measurements' },
    { value: 'drawing_nominal',     label: 'Process target / drawing nominal' },
    { value: 'not_established',     label: 'Not established yet' },
];

const inputSx = {
    '& .MuiOutlinedInput-root': {
        color: 'rgba(255,255,255,0.9)',
        '& fieldset':             { borderColor: 'rgba(255,255,255,0.23)' },
        '&:hover fieldset':       { borderColor: 'rgba(255,255,255,0.5)' },
        '&.Mui-focused fieldset': { borderColor: 'rgba(144,202,249,0.7)' },
        '&.Mui-disabled':         { color: 'rgba(255,255,255,0.3)' },
        '&.Mui-disabled fieldset':{ borderColor: 'rgba(255,255,255,0.12)' },
    },
    '& .MuiInputLabel-root':            { color: 'rgba(255,255,255,0.6)' },
    '& .MuiInputLabel-root.Mui-focused':{ color: 'rgba(144,202,249,0.9)' },
    '& .MuiInputLabel-root.Mui-disabled':{ color: 'rgba(255,255,255,0.3)' },
};

const selectSx = {
    color: 'rgba(255,255,255,0.9)',
    '& .MuiOutlinedInput-notchedOutline':        { borderColor: 'rgba(255,255,255,0.23)' },
    '&:hover .MuiOutlinedInput-notchedOutline':  { borderColor: 'rgba(255,255,255,0.5)' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(144,202,249,0.7)' },
    '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.6)' },
};

function Type1GRRForm({ values, onChange }) {
    const [advancedOpen, setAdvancedOpen] = useState(false);

    const set = (field) => (e) => onChange({ ...values, [field]: e.target.value });

    const noReference = values.referenceConfidence === 'not_established';
    const drawingNominal = values.referenceConfidence === 'drawing_nominal';
    const priorAverage   = values.referenceConfidence === 'prior_average';

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {/* Study Description */}
            <TextField
                label="Study Description"
                value={values.description}
                onChange={set('description')}
                size="small"
                fullWidth
                sx={inputSx}
                inputProps={{ maxLength: 500 }}
            />

            {/* Reference Classification */}
            <FormControl size="small" fullWidth>
                <InputLabel sx={{ color: 'rgba(255,255,255,0.6)', '&.Mui-focused': { color: 'rgba(144,202,249,0.9)' } }}>
                    Reference Classification *
                </InputLabel>
                <Select
                    value={values.referenceConfidence}
                    label="Reference Classification *"
                    onChange={(e) => {
                        const next = e.target.value;
                        onChange({
                            ...values,
                            referenceConfidence: next,
                            // Clear reference value when "not established"
                            referenceValue: next === 'not_established' ? '' : values.referenceValue,
                        });
                    }}
                    sx={selectSx}
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                backgroundColor: '#1e242c',
                                border: '1px solid rgba(255,255,255,0.12)',
                                '& .MuiMenuItem-root': { color: 'rgba(255,255,255,0.85)', fontSize: '0.85rem' },
                                '& .MuiMenuItem-root:hover': { backgroundColor: 'rgba(144,202,249,0.08)' },
                                '& .MuiMenuItem-root.Mui-selected': { backgroundColor: 'rgba(144,202,249,0.12)' },
                            },
                        },
                    }}
                >
                    {REFERENCE_CONFIDENCE_OPTIONS.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                </Select>
            </FormControl>

            {/* Drawing nominal warning */}
            {drawingNominal && (
                <Typography variant="caption" sx={{ color: '#ffb74d', display: 'block', mt: -0.5, lineHeight: 1.4 }}>
                    Using a nominal as reference conflates measurement error with product conformance. Bias results should be interpreted with caution.
                </Typography>
            )}

            {/* Prior average warning */}
            {priorAverage && (
                <Typography variant="caption" sx={{ color: '#ffb74d', display: 'block', mt: -0.5, lineHeight: 1.4 }}>
                    Reference derived from prior measurements inherits that study's uncertainty. Bias is shown with a warning.
                </Typography>
            )}

            {/* Reference Value */}
            <TextField
                label="Reference Value"
                value={values.referenceValue}
                onChange={set('referenceValue')}
                size="small"
                fullWidth
                disabled={noReference}
                type="number"
                inputProps={{ inputMode: 'decimal', step: 'any' }}
                helperText={noReference ? 'Disabled — reference not established' : undefined}
                sx={inputSx}
                FormHelperTextProps={{ sx: { color: 'rgba(255,255,255,0.35)', mx: 0 } }}
            />

            {/* Tolerance */}
            <TextField
                label="Tolerance (USL − LSL)"
                value={values.tolerance}
                onChange={set('tolerance')}
                size="small"
                fullWidth
                type="number"
                inputProps={{ inputMode: 'decimal', step: 'any', min: 0 }}
                sx={inputSx}
            />

            {/* Advanced section */}
            <Button
                size="small"
                onClick={() => setAdvancedOpen(v => !v)}
                endIcon={advancedOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ alignSelf: 'flex-start', color: 'rgba(255,255,255,0.45)', fontSize: '0.72rem', px: 0, '&:hover': { color: '#90caf9' } }}
            >
                Advanced
            </Button>
            <Collapse in={advancedOpen}>
                <Box sx={{ pl: 0.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <TextField
                        label="k factor (Cg/Cgk)"
                        value={values.k}
                        onChange={set('k')}
                        size="small"
                        fullWidth
                        type="number"
                        inputProps={{ inputMode: 'decimal', step: '0.01', min: '0.01', max: '1' }}
                        helperText="Default 0.20 — AIAG 20% of tolerance rule"
                        sx={inputSx}
                        FormHelperTextProps={{ sx: { color: 'rgba(255,255,255,0.35)', mx: 0 } }}
                    />
                </Box>
            </Collapse>
        </Box>
    );
}

export const DEFAULT_FORM_VALUES = {
    description:          '',
    referenceConfidence:  '',
    referenceValue:       '',
    tolerance:            '',
    k:                    0.2,
};

export default Type1GRRForm;
